"use client"

import { useEffect, useState, useRef, useCallback } from "react"
import { Container, Row, Col, Card, Form, Button, Spinner } from "react-bootstrap"
import { FaEnvelope, FaUniversity, FaGraduationCap, FaBook } from "react-icons/fa"
import Login from "./Login"
import ExportButtons from "./ExportButtons"

const ITEMS_PER_BATCH = 9

const UsersList = () => {
  const [files, setFiles] = useState([])
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [itemsToShow, setItemsToShow] = useState(ITEMS_PER_BATCH)

  // Referencia para el observador de intersección
  const observer = useRef()
  // Referencia para el último elemento de la lista
  const lastElementRef = useRef()

  useEffect(() => {
    if (isAuthenticated) {
      setIsLoading(true)
      fetch("/api/registros")
        .then((response) => {
          if (!response.ok) {
            throw new Error("Network response was not ok")
          }
          return response.json()
        })
        .then((data) => {
          setFiles(data)
          setIsLoading(false)
        })
        .catch((error) => {
          console.error("Error al recuperar archivos:", error)
          setIsLoading(false)
        })
    }
  }, [isAuthenticated])

  const handleLogin = () => {
    setIsAuthenticated(true)
  }

  const filteredFiles = files.filter(
    (file) =>
      file.userData.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      file.userData.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      file.userData.email.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  // Mostrar solo la cantidad de elementos definida en itemsToShow
  const currentItems = filteredFiles.slice(0, itemsToShow)

  // Función para cargar más elementos
  const loadMoreItems = useCallback(() => {
    if (itemsToShow < filteredFiles.length) {
      setItemsToShow((prevItems) => prevItems + ITEMS_PER_BATCH)
    }
  }, [itemsToShow, filteredFiles.length])

  // Configurar el observador de intersección
  useEffect(() => {
    // Desconectar el observador anterior si existe
    if (observer.current) {
      observer.current.disconnect()
    }

    // Callback para cuando el elemento es visible
    const handleObserver = (entries) => {
      const [entry] = entries
      // Comenzar a cargar cuando el elemento está cerca de ser visible
      if (entry.isIntersecting && itemsToShow < filteredFiles.length) {
        // Añadir un pequeño retraso para que el usuario vea el loader
        setTimeout(() => {
          loadMoreItems()
        }, 800)
      }
    }

    // Crear nuevo observador
    observer.current = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: "200px", // Aumentado de 20px a 200px para que comience a cargar antes
      threshold: 0.1,
    })

    // Observar el último elemento si existe
    if (lastElementRef.current) {
      observer.current.observe(lastElementRef.current)
    }

    return () => {
      if (observer.current) {
        observer.current.disconnect()
      }
    }
  }, [loadMoreItems, isLoading, filteredFiles.length, itemsToShow])

  // Resetear itemsToShow cuando cambia el término de búsqueda
  useEffect(() => {
    setItemsToShow(ITEMS_PER_BATCH)
  }, [searchTerm])

  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />
  }

  return (
    <>
      <style jsx>{`
        .loader-container {
          position: relative;
          min-height: 120px;
          margin-top: 2rem;
          margin-bottom: 2rem;
          background-color: rgba(248, 249, 250, 0.7);
          border-radius: 8px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
        }
        
        .loader-wrapper {
          padding: 1.5rem;
        }
        
        .loader-text {
          font-size: 1rem;
          font-weight: 500;
          color: #0d6efd;
        }
        
        @keyframes pulse {
          0% { opacity: 0.6; }
          50% { opacity: 1; }
          100% { opacity: 0.6; }
        }
        
        .loader-text {
          animation: pulse 1.5s infinite ease-in-out;
        }
      `}</style>
      <Container style={{ marginTop: "5rem" }}>
        <h1 className="text-center mb-4">Postulantes Registrados</h1>
        <Row className="mb-4">
          <Col>
            <ExportButtons />
          </Col>
        </Row>
        <Row className="mb-4">
          <Col md={{ span: 6, offset: 3 }}>
            <Form.Group controlId="searchUsers">
              <Form.Control
                type="text"
                placeholder="Buscar postulantes..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value)
                }}
              />
            </Form.Group>
          </Col>
        </Row>
        {isLoading && currentItems.length === 0 ? (
          <div className="text-center">
            <Spinner animation="border" role="status" variant="primary">
              <span className="visually-hidden">Cargando...</span>
            </Spinner>
            <p className="mt-2">Cargando postulantes...</p>
          </div>
        ) : (
          <>
            {filteredFiles.length === 0 && <p className="text-center text-muted">No se encontraron postulantes.</p>}
            <Row xs={1} md={2} lg={3} className="g-4">
              {currentItems.map((file, index) => {
                // Determinar si este es el último elemento para agregar la referencia
                const isLastElement = index === currentItems.length - 1

                return (
                  <Col key={file._id} ref={isLastElement ? lastElementRef : null}>
                    <Card className="h-100 shadow-sm">
                      <Card.Header className="bg-primary text-white">
                        <Card.Title>
                          {file.userData.firstName} {file.userData.lastName}
                        </Card.Title>
                      </Card.Header>
                      <Card.Body>
                        <Card.Text>
                          <FaEnvelope className="me-2" />
                          {file.userData.email}
                        </Card.Text>
                        <Card.Text>
                          <FaUniversity className="me-2" />
                          {file.userData.university}
                        </Card.Text>
                        <Card.Text>
                          <FaGraduationCap className="me-2" />
                          {file.userData.major}
                        </Card.Text>
                        <div className="mt-3">
                          <strong>
                            <FaBook className="me-2" />
                            VML Academy:
                          </strong>
                          <div
                            style={{
                              height: "100px",
                              overflowY: "auto",
                              border: "1px solid #dee2e6",
                              borderRadius: "0.25rem",
                              padding: "0.5rem",
                              marginTop: "0.5rem",
                            }}
                          >
                            {file.userData.motivation}
                          </div>
                        </div>
                      </Card.Body>
                      <Card.Footer className="bg-white border-top-0">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          href={file.filePath}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Ver archivo
                        </Button>
                      </Card.Footer>
                    </Card>
                  </Col>
                )
              })}
            </Row>
            {!isLoading && currentItems.length > 0 && currentItems.length < filteredFiles.length && (
              <div className="text-center my-5 py-4 loader-container">
                <div className="loader-wrapper">
                  <Spinner animation="border" role="status" variant="primary" style={{ width: "3rem", height: "3rem" }}>
                    <span className="visually-hidden">Cargando más...</span>
                  </Spinner>
                  <p className="mt-3 loader-text">Cargando más postulantes...</p>
                </div>
              </div>
            )}
          </>
        )}
      </Container>
    </>
  )
}

export default UsersList
